import { useState, useCallback, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { setGlobalEvents, setIsEventCalledByName } from "JS/Redux/Event";
import { TicketError, TicketsService } from "JS/Services/Events";
import { Event } from "JS/Models/Events";
import { messaging } from "JS/Helpers/UserMessaging";
import { sortArrayByKey } from "JS/Helpers";
import { setGlobalHomeNavStack } from "JS/Redux/NavStack";
import { useNavigation } from "./NavigationEngine";
import { IEventDetailsByFolderName } from "JS/Models/NavigationModels";

export const service = new TicketsService();

export const useGetEvents = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<Event[]>([]);
    const [isErrorNoEvents, setErrorNoEvents] = useState(false);
    const [isErrorNoRegistered, setErrorNoRegistered] = useState(false);

    const dispatch = useAppDispatch();

    const refetch = useCallback(() => {
        setLoading(true);
        return service
            .getEvents()
            .then(async (res) => {
                const sortedEvents = sortArrayByKey(res, "EventName", "ASC");
                dispatch(
                    setGlobalEvents({
                        eventsList: sortedEvents,
                    }),
                );
                dispatch(
                    setIsEventCalledByName({
                        isEventCalled: false,
                        eventFolderName: "",
                    }),
                );
                setResponse(sortedEvents);
                return sortedEvents;
            })
            .catch((err) => {
                setErrorNoEvents(err === TicketError.NoEvents);
                setErrorNoRegistered(err === TicketError.NoneRegistered);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) {
            refetch();
        }
    }, [skip]);

    return {
        refetch,
        response,
        loading,
        isErrorNoEvents,
        isErrorNoRegistered,
    };
};

export const useGetEventByName = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<Event[]>([]);
    const [isErrorNoEvents, setErrorNoEvents] = useState(false);
    const [isErrorNoRegistered, setErrorNoRegistered] = useState(false);
    const [isErrorNoTickets, setIsErrorNoTickets] = useState(false);
    const { eventDetailNavigation } = useNavigation();
    const dispatch = useAppDispatch();

    const refetch = useCallback((actionDetails: IEventDetailsByFolderName) => {
        setLoading(true);
        return service
            .getEventByName(actionDetails.eventFolderName)
            .then(async (res) => {
                dispatch(
                    setGlobalEvents({
                        eventsList: res,
                    }),
                );
                dispatch(
                    setIsEventCalledByName({
                        isEventCalled: true,
                        eventFolderName: actionDetails.eventFolderName,
                    }),
                );
                setResponse(res);
                setGlobalHomeNavStack({ isHomeTrack: true });
                eventDetailNavigation({ eventName: res[0].EventName });
                return res;
            })
            .catch((err) => {
                if (err === "No tickets found") {
                    setIsErrorNoTickets(true);
                } else {
                    enqueueSnackbar(
                        err.message || "Event is not currently active",
                        {
                            variant: "error",
                        },
                    );
                }
                setErrorNoEvents(err === TicketError.NoEvents);
                setErrorNoRegistered(err === TicketError.NoneRegistered);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return {
        refetch,
        response,
        loading,
        isErrorNoTickets,
        setIsErrorNoTickets,
        isErrorNoEvents,
        isErrorNoRegistered,
    };
};

export const useEventFetcher = (flag, eventList) => {
    const byNameHook = useGetEventByName();
    const eventsHook = useGetEvents(eventList && eventList.length > 0);

    const activeHook = flag ? byNameHook : eventsHook;

    const refetch = (flag, eventFolderName: string) => {
        if (flag) {
            return byNameHook.refetch({ eventFolderName, actionType: "" });
        } else {
            return eventsHook.refetch();
        }
    };

    return {
        loading: activeHook.loading,
        isErrorNoEvents: activeHook.isErrorNoEvents,
        isErrorNoRegistered: activeHook.isErrorNoRegistered,
        refetch,
    };
};

export const useGetBarCode = (
    userId: string,
    isPrimaryUser: number,
    skip?: boolean,
) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<string>("");

    const { enqueueSnackbar } = useSnackbar();

    const refetch = useCallback((id: string, isPrimaryUser: number) => {
        setLoading(true);

        return service
            .getBarCode(id, isPrimaryUser)
            .then((response) => {
                setResponse(response.data);
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip && userId) {
            refetch(userId, isPrimaryUser);
        }
    }, [skip, userId, isPrimaryUser]);

    return {
        refetch,
        response,
        loading,
    };
};

export const useGetPdfBlob = (url: string, skip?: boolean) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<Blob>(null);

    const refetch = useCallback((path: string) => {
        setLoading(true);

        return service
            .getPdfBlob(path)
            .then(async (res) => {
                setResponse(res);
                return res;
            })
            .catch((err) => {
                return new Blob([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip && url) {
            refetch(url);
        }
    }, [skip, url]);

    return {
        refetch,
        response,
        loading,
    };
};

export const useGetProfileImage = (userId: string, skip?: boolean) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [imageExists, setImageExists] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [imageExpiry, setImageExpiry] = useState<number>(0);

    const refetch = useCallback((id: string) => {
        setLoading(true);
        return service
            .getProfileImage(id)
            .then((res) => {
                setImageExists(res.imageExists);
                setImageUrl(res.profileImageUrl);
                setImageExpiry(res.profileImageExpiry);
                return res;
            })
            .catch(() => {
                setImageExists(false);
                setImageUrl(null);
                return { imageExists: false, profileImageUrl: null, profileImageExpiry: 0 };
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip && userId) {
            refetch(userId);
        }
    }, [skip, userId]);

    return {
        refetch,
        response: imageExists,
        imageUrl,
        imageExpiry,
        loading,
    };
};

export const useSetProfileImage = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();

    const refetch = useCallback(
        async (file: any, fileName: string, isPrimaryUser: number) => {
            setLoading(true);
            return service
                .getS3PostFileUrl(fileName, isPrimaryUser)
                .then(async (res) => {
                    return service
                        .setProfilePhotoInS3(file, res.data)
                        .then((result) => {
                            if (result.status === 200) {
                                enqueueSnackbar(
                                    messaging?.events?.uploadedsuccesfully,
                                    {
                                        variant: "success",
                                    },
                                );
                                setResponse(true);
                                return true;
                            } else {
                                setResponse(false);
                                return false;
                            }
                        })
                        .catch((err) => {
                            setResponse(false);
                            enqueueSnackbar(err.message, {
                                variant: "error",
                            });
                            return false;
                        });
                })
                .catch((err) => {
                    setResponse(false);
                    enqueueSnackbar(err.message, {
                        variant: "error",
                    });
                    return false;
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [],
    );

    return {
        refetch,
        response,
        loading,
    };
};

export const useGlobalEvents = () => {
    const dispatch = useAppDispatch();

    const allData = useAppSelector((state) => state.events);

    return {
        events: useAppSelector((state) => state.events)?.eventsList,
        eventCalledByName: useAppSelector((state) => state.events)
            ?.eventCalledByName,
        setGlobalEvents: (events: Event[]) =>
            dispatch(
                setGlobalEvents({
                    ...allData,
                    eventsList: events,
                }),
            ),
    };
};
